import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API_URL } from "../../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, EyeOff, Info, CheckCircle2, AlertTriangle } from "lucide-react";

const TYPE_META = {
  info: { label: "Informação", icon: Info, cls: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  success: { label: "Sucesso", icon: CheckCircle2, cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  warning: { label: "Aviso", icon: AlertTriangle, cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "—");

export const MessagesTab = ({ headers }) => {
  const [messages, setMessages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ company_id: "all", type: "info", title: "", body: "" });
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [mRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/backoffice/messages`, { headers }),
        axios.get(`${API_URL}/backoffice/companies`, { headers }),
      ]);
      setMessages(mRes.data);
      setCompanies(cRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao carregar mensagens"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendMessage = async () => {
    if (!form.title.trim()) { toast.error("Escreva o título da mensagem"); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/backoffice/messages`, {
        title: form.title,
        body: form.body,
        type: form.type,
        company_id: form.company_id === "all" ? null : form.company_id,
      }, { headers });
      toast.success("Mensagem enviada — vai aparecer no sistema do(s) cliente(s)");
      setForm({ company_id: "all", type: "info", title: "", body: "" });
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar"); }
    finally { setSending(false); }
  };

  const deactivate = async (m) => {
    try {
      await axios.delete(`${API_URL}/backoffice/messages/${m.message_id}`, { headers });
      toast.success("Mensagem desativada");
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao desativar"); }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/[0.04] p-5 space-y-4" data-testid="message-create-card">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          <p className="font-bold">Enviar Mensagem aos Clientes</p>
        </div>
        <p className="text-sm text-white/50">
          A mensagem aparece como notificação no topo do sistema do cliente até ser dispensada por cada utilizador.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Destino</Label>
            <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger className="bg-white/5 border-white/15 text-white" data-testid="message-company-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Todas as empresas</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="bg-white/5 border-white/15 text-white" data-testid="message-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Informação</SelectItem>
                <SelectItem value="success">Sucesso (ex: pagamento recebido)</SelectItem>
                <SelectItem value="warning">Aviso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/70 text-xs">Título</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Pagamento recebido — subscrição ativada" className="bg-white/5 border-white/15 text-white placeholder:text-white/25" data-testid="message-title-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/70 text-xs">Mensagem (opcional)</Label>
          <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} placeholder="Detalhes adicionais..." className="bg-white/5 border-white/15 text-white placeholder:text-white/25" data-testid="message-body-input" />
        </div>
        <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" disabled={sending} onClick={sendMessage} data-testid="send-message-btn">
          <Send className="w-4 h-4 mr-1.5" /> {sending ? "A enviar..." : "Enviar Mensagem"}
        </Button>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-white/60">Mensagens enviadas ({messages.length})</p>
        {messages.map((m) => {
          const meta = TYPE_META[m.type] || TYPE_META.info;
          return (
            <div key={m.message_id} className={`rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-3 ${!m.active ? "opacity-50" : ""}`} data-testid={`message-row-${m.message_id}`}>
              <meta.icon className={`w-4 h-4 shrink-0 ${meta.cls.split(" ")[1]}`} />
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-sm">{m.title}</p>
                {m.body && <p className="text-xs text-white/50">{m.body}</p>}
                <p className="text-[11px] text-white/30 mt-0.5">
                  {m.company_name ? `Para: ${m.company_name}` : "Todas as empresas"} · {fmtDate(m.created_at)} · dispensada por {m.dismissed_count || 0} utilizador(es)
                </p>
              </div>
              <Badge className={meta.cls}>{meta.label}</Badge>
              {m.active ? (
                <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white/60 hover:bg-white/10 hover:text-white" onClick={() => deactivate(m)} data-testid={`deactivate-message-${m.message_id}`}>
                  <EyeOff className="w-3.5 h-3.5 mr-1" /> Desativar
                </Button>
              ) : (
                <Badge className="bg-white/10 text-white/40">Inativa</Badge>
              )}
            </div>
          );
        })}
        {messages.length === 0 && <p className="text-center text-white/40 py-8">Ainda não enviou mensagens</p>}
      </div>
    </div>
  );
};
